import React, {useState, useMemo, useRef, forwardRef, useEffect} from 'react';

import GroupedValueGrid from './GroupedValueGrid.jsx';
import { MenuListItem, IfNoItems } from '../PresetsPanel.jsx';

import { fetchJson } from '../../../helpers/fetch.js';
import { WithPermission } from '../Toolbar.jsx';
import { Dialog, DialogHeader } from '../../bootstrap/index.jsx';
import { DialogFooter } from '../ColumnEdit.jsx';
import { FaIcon } from '../../common/Icons.jsx';
import AgGrid from '../../common/AgGrid/index.jsx';
import Select from '../../common/Select.jsx';

import {
  datagrid_grouped_value_create_path,
  datagrid_grouped_value_update_path
} from '~/routes';

const CheckBox = ({checked}) => <div 
  style={{marginRight: 'var(--ag-widget-horizontal-spacing)'}} 
  className={`ag-wrapper ag-input-wrapper ag-checkbox-input-wrapper ${checked ? 'ag-checked' : ''}`} 
  role="presentation"
>
  <input className="ag-input-field-input ag-checkbox-input" type="checkbox" tabIndex="-1" />
</div>

const HoverCog = (props) => {
  return <FaIcon {...props} type='cog' className='dg-link-hover dg-on-hover'/>

}

const columnDefsWithGroupValues = (existingColumns, groupValueItem) => {
  const newDefs = [
    ...existingColumns.filter(c => !c?.groupId?.startsWith('group-value-')), 
    {
      groupId: `group-value-${groupValueItem}`,
      headerName: groupValueItem.name,
      marryChildren: true,
      children: groupValueItem.column_defs.map(c => (
        {
          ...c, // headerName, field
          aggFunc: 'pre' // pre calculated / set
        }
      ))
    }
  ]
  return newDefs
}

const GroupValueItems = ({items, openEditMode, permissions, api, context}) => {

  const [checkedItem, setCheckedItem] = useState(null);

  const onCreate = () => {openEditMode(undefined)}
  const onEdit = (id) => {openEditMode(id)}
  const onCheck = (item) => {
    const columnDefs = api.getColumnDefs();
    api.setGridOption('context', { ...context, get_rows_params: {...(context.get_rows_params || {}), group_value_id: item.id } });
    api.setGridOption('columnDefs', columnDefsWithGroupValues(columnDefs, item));
    setCheckedItem(item.name)
  }

  return <div className='dg-separated'>
    <div className='dg-header d-flex' style={{justifyContent: 'space-between'}}>
      <b>{I18n.t('group_values')}</b> 
    </div>
    <IfNoItems items={items}>{I18n.t('no_grouped_values')}</IfNoItems>
    {items.map(item => 
      <MenuListItem 
        preElement={<CheckBox checked={checkedItem == item.name} />}
        key={'grouped-value-' + item.id} 
        label={item.name}
        endElement={<HoverCog onClick={() => onEdit(item.id)} />}
        onClick={() => onCheck(item)}
      />
    )}
    
    <WithPermission permissions={permissions} permission='group_value_manage'>
      <div className='dg-link-hover dg-header dg-top-padding' onClick={onCreate}>
        <span>{I18n.t('grouped_values_create')}</span>
      </div>
    </WithPermission>
  </div>
}

const GroupValuePanel = (props) => {
  const {datagrid, context} = props;
  const { permissions, context_name } = context;

  // Items can be handled uncontrolled here, the component is mounted once in view
  const [items, setItems] = useState(datagrid.group_values);
  const dialogRef = useRef(null);

  const [showManagementModal, setShowManagementModal] = useState(null); // null | undefined | id

  const onEdit = (value) => {
    setShowManagementModal(value);
  }

  const handleSave = () => {
    let postData = dialogRef.current.getState();
    const isCreate = !postData.id;
    const action = isCreate ? 
      datagrid_grouped_value_create_path({context_name: context_name, _options: true}) :
      datagrid_grouped_value_update_path({id: postData.id, context_name: context_name, _options: true})

    fetchJson(action, {method: 'POST', body: JSON.stringify(postData)}).then(res => {
      onClose();
      let newItems = [...items]
      if (isCreate) {
        newItems.push(res);
      } else {
        newItems[newItems.findIndex(i => i.id == parseInt(postData.id,10))] = res;
      }
      setItems(newItems);
    })
  }

  const onClose = () => setShowManagementModal(null);

  return <WithPermission permissions={permissions} permission='group_value_list'>
    { showManagementModal !== null && 
      <GridDialog 
        id={showManagementModal}
        handleClose={onClose}
        handleSave={handleSave}
        {...props}
        ref={dialogRef}
      />
    }
    <GroupValueItems
      {...props}
      openEditMode={onEdit}
      items={items} 
      permissions={permissions}
    />
  </WithPermission>

} 

const GridDialog = forwardRef(({id, api, columnApi, datagrid, context, handleClose, handleSave}, dialogRef) => {

  const isCreate = id === undefined;
  const groupSelectGrid = useRef(null);
  const [next, setNext] = useState(false);

  const handleNext = () => {
    // TODO: Indicate to user why can't continue
    if (groupSelectGrid.current.api.getRowGroupColumns().length == 0) return;
    setNext(true);
  }

  // Coldefs from parent grid
  const initialColumnDefs = api.getColumnDefs();

  const addNewSelection = (colKey) => {
    const api = groupSelectGrid.current.api;
    let keys = [...api.getRowGroupColumns().map(a => a.getColId())]
    if (keys.includes(colKey)) {
      return;
    }
    api.setRowGroupColumns([...keys, colKey]);
  }

  const columnsWithOptions = initialColumnDefs
    .filter(f => (f.cellEditorParams?.options || []).length > 0)
    .map(c => ({label: c.headerName, value: c.field}))

  if (isCreate && !next) {
    return <Dialog 
      header={<DialogHeader onClose={handleClose} title='' showBorder={false} />} 
      footer={
        <DialogFooter 
          onClose={handleClose} 
          onSubmit={handleNext} 
          submitText={I18n.t('next')} 
        />}
        size='md'
        closeOnOutsideClick={false}
        onClose={handleClose}
      >
      <label>{I18n.t('select_row_groups')}</label>
        <Select 
          options={columnsWithOptions}
          onChange={addNewSelection}
          className={'mb-1'}
          value={[]}
        />
        <AgGrid 
          ref={groupSelectGrid}
          rowGroupPanelShow={'always'}
          columnDefs={initialColumnDefs}
          localeText={{rowGroupColumnsEmptyMessage: 'Select row groups above'}}
        />
    </Dialog>
  }

  return <Dialog 
    header={<DialogHeader onClose={handleClose} title='' showBorder={false} />} 
    footer={
      <DialogFooter 
        onClose={handleClose} 
        onSubmit={handleSave} 
        submitText={isCreate ? I18n.t('add') : I18n.t('save')} 
        handleDeleteCol={(e) => handleDeleteCol(e, values.id)} 
        deletable={true}
        isCreate={isCreate}
      />}
    size='xl'
    closeOnOutsideClick={false}
    onClose={handleClose}
  >
    <GroupedValueGrid
      ref={dialogRef}
      id={id}
      datagrid={datagrid}
      context={context}
      parentGridColDefs={initialColumnDefs}
      colState={groupSelectGrid.current?.api?.getColumnState()}
      onClose={handleClose} 
    />
  </Dialog>
})

export default GroupValuePanel;